import { FunctionComponent } from "react";

import {
	faTrash,
	faCheck,
	faXmark,
	faArrowRight,
	faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Toggle } from "@/components/Toggle/Toggle";
import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { InputField } from "@/components/InputField/InputField";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import {
	ZohoFieldMapping,
	ZohoField,
	ZohoSourceField,
	FieldMappingSourceType,
	FieldMappingAiConfig,
} from "../schema/zoho";
import {
	AiHintBanner,
	AiSourceCell,
	CannotMapRow,
	SourceTypePills,
	TargetFieldBadges,
} from "./fieldMapping";

type Ts = (key: string, options?: Record<string, unknown>) => string;

const buildBadgeLabels = (ts: Ts) => ({
	required: ts("metaBadge.required"),
	multi: ts("metaBadge.multi"),
	dropdown: ts("metaBadge.dropdown"),
	number: ts("metaBadge.number"),
	calculated: ts("metaBadge.calculated"),
	readOnly: ts("metaBadge.readOnly"),
});

const isFieldUnwritable = (field: ZohoField | undefined): boolean =>
	Boolean(field?.isReadOnly) || Boolean(field?.isCalculated);

// --- MappingRow (read-only saved mapping) ---

interface MappingRowProps {
	mapping: ZohoFieldMapping;
	sourceFields: ZohoSourceField[];
	zohoFields: ZohoField[];
	onToggleActive: (id: number, isActive: boolean) => void;
	onDelete: (id: number) => void;
	onConfigureAi: (mapping: ZohoFieldMapping) => void;
	ts: Ts;
}

export const ZohoMappingRow: FunctionComponent<MappingRowProps> = ({
	mapping,
	sourceFields,
	zohoFields,
	onToggleActive,
	onDelete,
	onConfigureAi,
	ts,
}) => {
	const isConstant = mapping.sourceType === FieldMappingSourceType.CONSTANT;
	const isAi = mapping.sourceType === FieldMappingSourceType.AI;
	const zohoField = zohoFields.find((f) => f.name === mapping.zohoField);
	const zohoLabel = zohoField
		? `${zohoField.label}${zohoField.isCustom ? " (Custom)" : ""}`
		: mapping.zohoField;
	const badgeLabels = buildBadgeLabels(ts);

	if (isFieldUnwritable(zohoField)) {
		return (
			<CannotMapRow
				badgeLabels={badgeLabels}
				cannotMapLabel={ts("cannotMap")}
				targetField={zohoField || {}}
				targetFieldLabel={zohoLabel}
				testIdPrefix="zoho"
				tooltip={ts("cannotMapTooltip")}
				onDelete={() => onDelete(mapping.id)}
			/>
		);
	}

	const sourceLabel = isConstant
		? ts("constantLabel", { value: mapping.sourceField })
		: sourceFields.find((f) => f.name === mapping.sourceField)?.label || mapping.sourceField;

	return (
		<div className="border-b border-gray-100">
			<div className="flex items-center gap-3 py-3">
				<div className="flex-1">
					{isAi ? (
						<AiSourceCell
							badgeLabel={ts("aiBadge")}
							configureLabel={ts("aiConfigure")}
							creditTooltip={ts("aiCreditTooltip")}
							label={zohoLabel}
							testIdPrefix="zoho"
							onConfigure={() => onConfigureAi(mapping)}
						/>
					) : (
						<div
							className={classNames(
								"py-[9px] px-3 rounded-xl text-ssm border border-border-light bg-gray-50 text-text",
								isConstant && "italic text-text-meta"
							)}
							data-testid={isConstant ? "zoho-constant-label" : "zoho-field-label"}
						>
							{sourceLabel}
						</div>
					)}
				</div>
				<div className="w-[24px] flex items-center justify-center">
					<Icon className="w-[16px] h-[16px] text-gray-400" icon={faArrowRight} />
				</div>
				<div className="flex-1">
					<div className="py-[9px] px-3 rounded-xl text-ssm border border-border-light bg-gray-50 text-text flex items-center gap-2">
						<span className="flex-1 truncate">{zohoLabel}</span>
						<TargetFieldBadges field={zohoField || {}} labels={badgeLabels} testIdPrefix="zoho" />
					</div>
				</div>
				<div className="w-[60px] flex items-center justify-center">
					<Toggle
						isChecked={mapping.isActive}
						onChange={() => onToggleActive(mapping.id, !mapping.isActive)}
					/>
				</div>
				<div
					className="w-fit bg-status-error-light flex justify-end p-1 rounded border-border-light group hover:border-text-status-error cursor-pointer"
					onClick={() => onDelete(mapping.id)}
				>
					<Icon className="text-status-error cursor-pointer w-4 h-4" icon={faTrash} />
				</div>
			</div>
			{mapping.searchRefusedAt && (
				// Zoho refused to search this field, so duplicates are matched on email and name only (PREC-4354).
				<p
					className="flex items-start gap-2 pb-3 text-ssm text-text-warning"
					data-testid="zoho-search-refused-warning"
				>
					<Icon className="w-4 h-4 mt-[2px] shrink-0" icon={faTriangleExclamation} />
					<span>{ts("searchRefused")}</span>
				</p>
			)}
		</div>
	);
};

// --- NewMappingRow (unsaved editable mapping) ---

export interface NewZohoMapping {
	tempId: string;
	sourceField: string;
	sourceType: FieldMappingSourceType;
	aiConfig?: FieldMappingAiConfig;
	zohoField: string;
	isActive: boolean;
	entityType: string;
}

interface NewMappingRowProps {
	mapping: NewZohoMapping;
	sourceFields: ZohoSourceField[];
	zohoFields: ZohoField[];
	usedSourceFields: string[];
	usedZohoFieldsByType: Record<FieldMappingSourceType, string[]>;
	onUpdate: (tempId: string, data: Partial<NewZohoMapping>) => void;
	onSave: (mapping: NewZohoMapping) => void;
	onCancel: (tempId: string) => void;
	onConfigureAi: (mapping: NewZohoMapping) => void;
	isSaving: boolean;
	ts: Ts;
}

export const ZohoNewMappingRow: FunctionComponent<NewMappingRowProps> = ({
	mapping,
	sourceFields,
	zohoFields,
	usedSourceFields,
	usedZohoFieldsByType,
	onUpdate,
	onSave,
	onCancel,
	onConfigureAi,
	isSaving,
	ts,
}) => {
	const isConstant = mapping.sourceType === FieldMappingSourceType.CONSTANT;
	const isAi = mapping.sourceType === FieldMappingSourceType.AI;
	const usedZohoFields = usedZohoFieldsByType[mapping.sourceType] ?? [];

	const availableSourceFields = sourceFields.filter(
		(f) => f.name === mapping.sourceField || !usedSourceFields.includes(f.name)
	);
	const availableZohoFields = zohoFields.filter(
		(f) =>
			(f.name === mapping.zohoField || !usedZohoFields.includes(f.name)) && !isFieldUnwritable(f)
	);

	const sourceOptions: AutoCompleteItem[] = availableSourceFields.map((f) => ({
		id: f.name,
		title: f.label,
	}));

	const zohoOptions: AutoCompleteItem[] = availableZohoFields.map((f) => ({
		id: f.name,
		title: `${f.label}${f.isCustom ? " (Custom)" : ""}`,
	}));

	const selectedSource = sourceOptions.find((o) => o.id === mapping.sourceField);
	const selectedZoho = zohoOptions.find((o) => o.id === mapping.zohoField);
	const selectedZohoField = zohoFields.find((f) => f.name === mapping.zohoField);
	const canSave = isAi
		? Boolean(mapping.zohoField && mapping.aiConfig?.prompt)
		: Boolean(mapping.sourceField) && Boolean(mapping.zohoField);
	const saveDisabledTooltip = isAi && !mapping.aiConfig?.prompt ? ts("aiSaveDisabledTooltip") : "";
	const badgeLabels = buildBadgeLabels(ts);

	const handleSourceTypeChange = (next: FieldMappingSourceType) => {
		if (next === mapping.sourceType) {
			return;
		}

		onUpdate(mapping.tempId, { sourceType: next, sourceField: "", aiConfig: undefined });
	};

	return (
		<div className="flex flex-col gap-2 py-3 border-b border-gray-100 bg-gray-50 rounded-lg px-2">
			<div className="flex items-start gap-3">
				<div className="flex-1 flex flex-col gap-2">
					<SourceTypePills
						labels={{
							fromRecord: ts("sourceTypeFromRecord"),
							constant: ts("sourceTypeConstant"),
							ai: ts("sourceTypeAi"),
						}}
						testIdPrefix="zoho"
						value={mapping.sourceType}
						onChange={handleSourceTypeChange}
					/>
					{isAi ? (
						<AiSourceCell
							badgeLabel={ts("aiBadge")}
							configureDisabled={!mapping.zohoField}
							configureLabel={ts("aiConfigure")}
							creditTooltip={ts("aiCreditTooltip")}
							label={selectedZoho?.title || ""}
							placeholder={ts("aiTargetPlaceholder")}
							testIdPrefix="zoho"
							onConfigure={() => onConfigureAi(mapping)}
						/>
					) : isConstant ? (
						<InputField
							maxLength={255}
							name={`zoho-constant-${mapping.tempId}`}
							placeholder={ts("constantPlaceholder")}
							testId="zoho-constant-input"
							value={mapping.sourceField}
							handleChange={(e) => onUpdate(mapping.tempId, { sourceField: e.target.value })}
						/>
					) : (
						<AutoComplete
							data={sourceOptions}
							handleReset={() => onUpdate(mapping.tempId, { sourceField: "" })}
							handleSearch={() => {}}
							handleSelect={(item) => {
								if (item) {
									onUpdate(mapping.tempId, { sourceField: item.id as string });
								}
							}}
							placeholder={ts("selectField")}
							selectedItem={selectedSource?.title || ""}
						/>
					)}
				</div>
				<div className="w-[24px] flex items-center justify-center pt-9">
					<Icon className="w-[16px] h-[16px] text-gray-400" icon={faArrowRight} />
				</div>
				<div className="flex-1 pt-9 flex flex-col gap-1">
					<AutoComplete
						data={zohoOptions}
						handleReset={() =>
							onUpdate(mapping.tempId, {
								zohoField: "",
								sourceField: isAi ? "" : mapping.sourceField,
							})
						}
						handleSearch={() => {}}
						handleSelect={(item) => {
							if (item) {
								onUpdate(mapping.tempId, {
									zohoField: item.id as string,
									sourceField: isAi ? item.title : mapping.sourceField,
								});
							}
						}}
						placeholder={ts("selectField")}
						selectedItem={selectedZoho?.title || ""}
					/>
					{selectedZohoField && (
						<TargetFieldBadges
							field={selectedZohoField}
							labels={badgeLabels}
							testIdPrefix="zoho-new"
						/>
					)}
				</div>
				<div className="w-[60px] flex items-center justify-center pt-9">
					<Toggle
						isChecked={mapping.isActive}
						onChange={() => onUpdate(mapping.tempId, { isActive: !mapping.isActive })}
					/>
				</div>
				<div className="w-[80px] flex gap-2 pt-9">
					<Tooltip content={saveDisabledTooltip} disabled={!saveDisabledTooltip}>
						<div className="w-fit">
							<Button
								color={ButtonColor.ACTION}
								disabled={!canSave}
								image={
									<Icon className="w-4 h-4 min-w-4 min-h-4 shrink-0 text-white" icon={faCheck} />
								}
								isLoading={isSaving}
								size={ButtonSize.XS}
								testId="zoho-save-new-mapping"
								title=""
								onClick={() => onSave(mapping)}
							/>
						</div>
					</Tooltip>
					<div className="w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={
								<Icon className="w-4 h-4 min-w-4 min-h-4 shrink-0 text-gray-700" icon={faXmark} />
							}
							size={ButtonSize.XS}
							title=""
							onClick={() => onCancel(mapping.tempId)}
						/>
					</div>
				</div>
			</div>
			{isAi && (
				<AiHintBanner body={ts("aiHintBody")} testIdPrefix="zoho" title={ts("aiHintTitle")} />
			)}
		</div>
	);
};
